import { Datepicker, locale } from "@mobiscroll/react";
import { Divider, Radio, Spin } from "antd";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "react-native-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import { getPromoCodes } from "../../../../../redux/boxSlice";
import {
  closeModal,
  FLOW_MANAGER_MODAL,
} from "../../../../../redux/modalManagerSlice";
import { isMobile } from "../../../../../services/Helpers";
import { fetchApi } from "../../../../../services/HTTP";
import { t } from "../../../../../services/i18n";
import usePermissions from "../../../../../services/Permissions";
import {
  analyzeYupSchema,
  checkRequired,
} from "../../../../../services/stepperService";
import { Colors } from "../../../../../styles/Colors";
import AdvanceSelect from "../../../../common/AdvanceSelect";
import AppText from "../../../../common/AppText";
import CustomButtonV2 from "../../../../common/CustomButtonV2";
import FormInput from "../../../../common/FormInput";
import InfoTooltip from "../../../../common/InfoTooltip";
import Box from "../../../../common/Layout/Box";
import HStack from "../../../../common/Layout/HStack";
import VStack from "../../../../common/Layout/VStack";
import MultiSingleMenuWithSearch from "../../../../common/MultiSingleMenuWithSearch";
import {
  buildMembershipTypesPayload,
  createSelectedMembershipsInEditMode,
  filterMembershipsBySelectedLocation,
  getVisibleMembershipTypes,
  PROMO_CODE_MT_TYPE,
  PROMO_CODE_OPTION,
  reArrangeMembershipTypes,
  TIERED_PROMO_CODE_MAX,
} from "./PromoCodeHelpers";
import {GlobalConsts} from "../../../../../styles/GlobalConsts";

const LIMIT_PER_USER_MAX = 10;

const SELECTED_TOGGLE_STYLE = {
  backgroundColor: Colors.newDesign.blueBg,
  borderColor: Colors.newDesign.blueBg,
};

const PromoCode = (props) => {
  const { target, errors, values, setFieldValue, touched, validationSchema } = props;
  const box = useSelector((state) => state.box?.box);
  const lang = useSelector((state) => state.userSettings.lang);
  const [membershipTypes, setMembershipTypes] = useState(null);
  const [services, setServices] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [cycles, setCycles] = useState(values.recurring_cycles > 0 ? values.recurring_cycles : 1);
  const datePickerAnchorRef = useRef(null);

  const dispatch = useDispatch();
  const toast = useToast();
  const { has } = usePermissions();

  const isEditMode = useMemo(() => !!target, [target]);
  const validationObject = useMemo(() => analyzeYupSchema(validationSchema), [validationSchema],);
  const dateFormat = useMemo(() => (box ? box.locations_box[0].date_format : null), [box],);
  const locations = useMemo(() => box?.locations_box ?? [], [box]);
  const boxLocale = useMemo(() => (lang?.language === "Hebrew" ? "he" : "en"), [lang],);
  const allowRecurring = useMemo(() => has("recurringPaymentActions") && has("allowRecurringPromoCode"), [has],);
  const allowHugim = useMemo(() => has("hugim"), [has]);
  const allowTieredPromoCodes = useMemo(() => !allowHugim && has("tieredPromoCodes"), [allowHugim, has],);
  const tieredActive = useMemo(() => values.tiered_promo_codes !== null, [values.tiered_promo_codes],);

  const membershipTypesAllLocations = useMemo(() => reArrangeMembershipTypes(membershipTypes, services, allowHugim), [membershipTypes, services, allowHugim],);
  const membershipTypesWithRecurring = useMemo(() => filterMembershipsBySelectedLocation(membershipTypesAllLocations, values.location_id, locations,), [membershipTypesAllLocations, values.location_id, locations],);
  const visibleMembershipTypes = useMemo(() => getVisibleMembershipTypes(membershipTypesWithRecurring, values.recurring_cycles, allowHugim, tieredActive,), [membershipTypesWithRecurring, values.recurring_cycles, allowHugim, tieredActive,],);

  const limitPerUserOptions = useMemo(() => [{ value: PROMO_CODE_OPTION.UNLIMITED, label: t("unlimited") }, ...Array.from({ length: LIMIT_PER_USER_MAX }, (_, i) => ({value: i + 1, label: String(i + 1),})),], [],);
  const locationOptions = useMemo(() => {
    const mapped = locations.map((l) => ({ value: l.id, label: l.location }));
    if (locations.length <= 1) return mapped;
    return [{ value: null, label: t("promoNoLocation") }, ...mapped];
  }, [locations]);
  const showLocationField = useMemo(() => !!target && locations.length > 1, [target, locations],);

  useEffect(() => {
    getMembershipTypesAllLocations();
  }, []);

  useEffect(() => {
    if (!services || !membershipTypes) return;
    const useEditHydration = isEditMode && values.location_id === target.location_id;
    const selectedByType = useEditHydration ? createSelectedMembershipsInEditMode({ target, services, allMembershipTypes: membershipTypes, allowHugim }) : Object.fromEntries(Object.entries(visibleMembershipTypes).map(([type, items]) => [type, items.map((m) => m.id)]));
    setFieldValue("membership_types", buildMembershipTypesPayload(selectedByType, services));
  }, [services, membershipTypes, values.location_id, values.recurring_cycles === null, tieredActive]);

  const selectedIdsByType = useMemo(() => {
    const flat = values.membership_types ?? [];
    const out = {};
    Object.entries(visibleMembershipTypes).forEach(([type, items]) => {
      if (type === PROMO_CODE_MT_TYPE.SERVICE) {
        out[type] = items.filter((s) => s.membership_type_ids?.some((id) => flat.includes(id))).map((s) => s.id);
      } else {
        const ids = items.map((m) => m.id);
        out[type] = flat.filter((id) => ids.includes(id));
      }
    });
    return out;
  }, [visibleMembershipTypes, values.membership_types]);

  const getMembershipTypesAllLocations = async () => {
    try {
      await dispatch(getPromoCodes());
      const mt = await fetchApi("promoCode/getMembershipTypes", "GET", null, false, true,);
      setMembershipTypes(mt);
      const svc = await fetchApi("promoCode/getServices", "GET", null, false, true,);
      setServices(svc);
    } catch (error) {
      toast.show(t("somethingWentWrongPleaseTryAgain"), {type: "danger", duration: 4000,});
      dispatch(closeModal({ modalName: FLOW_MANAGER_MODAL }));
    }
  };

  const limitedButtonText = useMemo(() => {
    if (values.expiration_date) return moment(values.expiration_date, "YYYY-MM-DD").format(dateFormat);
    return t("limited");
  }, [values.expiration_date, dateFormat]);

  const onMembershipSelectChange = (type, ids) => {
    const items = visibleMembershipTypes[type] ?? [];
    const next = ids ?? [];
    const isService = type === PROMO_CODE_MT_TYPE.SERVICE;
    const oldFlatForType = isService ? items.flatMap((s) => s.membership_type_ids ?? []) : items.map((m) => m.id);
    const newFlatForType = isService ? next.flatMap((sid) => items.find((s) => s.id === sid)?.membership_type_ids ?? []) : next;
    const others = (values.membership_types ?? []).filter((id) => !oldFlatForType.includes(id));
    setFieldValue("membership_types", [...others, ...newFlatForType]);
  };

  const selectRecurringOption = (nextCycles) => {
    setFieldValue("recurring_cycles", nextCycles);
    if (tieredActive) {
      setFieldValue("tiered_promo_codes", null);
    }
  };

  const onCyclesChange = (n) => {
    const safe = n && n > 0 ? n : 1;
    setCycles(safe);
    if (values.recurring_cycles > 0) {
      setFieldValue("recurring_cycles", safe);
    }
  };

  const selectTieredPromoCodes = () => {
    setFieldValue("discount_amount", null);
    setFieldValue("recurring_cycles", 1);
    if (!tieredActive) {
      setFieldValue("tiered_promo_codes", [{ discount_amount: null, cycle: 1 }]);
    }
  };

  const addTieredPromoCode = () => {
    const current = values.tiered_promo_codes ?? [];
    if (current.length >= TIERED_PROMO_CODE_MAX) return;
    setFieldValue("tiered_promo_codes", [...current, { discount_amount: null, cycle: current.length + 1 },]);
  };

  const removeTieredPromoCode = (index) => {
    const next = (values.tiered_promo_codes ?? []).filter((_, i) => i !== index)
      .map((tpc, i) => ({ ...tpc, cycle: i + 1 }));
    setFieldValue("tiered_promo_codes", next.length > 0 ? next : null);
  };

  const setTieredPromoCodeDiscount = (index, discount) => {
    const next = [...(values.tiered_promo_codes ?? [])];
    next[index] = { ...next[index], discount_amount: discount };
    setFieldValue("tiered_promo_codes", next);
  };

  const changeRecurringRadio = (radio) => {
      if (radio === "all") selectRecurringOption(0);
      else if (radio === "first-n") selectRecurringOption(cycles);
      else selectTieredPromoCodes();
  }

  return (
    <VStack space={"0.5rem"} maxH={isMobile ? "100%" : "500px"} overflowY={"auto"}>
      <Box style={{display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", columnGap: "1rem", rowGap: "1rem",}}>
        <FormInput
          width={"100%"}
          label={`${t("code")}${checkRequired("name", validationObject)}`}
          placeholder={t("code")}
          maxLength={20}
          value={values.name}
          onChangeCallback={(v) => setFieldValue("name", v)}
          error={touched.name && errors.name ? errors.name : null}
        />

        <FormInput
          width={"100%"}
          label={t("expiringDate")}
          error={touched.expiration_date && errors.expiration_date ? errors.expiration_date : null}>
          <HStack space={"0.5rem"}>
            <Box flex={1}>
              <CustomButtonV2
                block
                wrapperStyle={{ width: "100%" }}
                style={!values.expiration_date ? SELECTED_TOGGLE_STYLE : undefined}
                text={t("unlimited")}
                onClick={() => setFieldValue("expiration_date", null)}
              />
            </Box>
            <Box flex={1} ref={datePickerAnchorRef}>
              <CustomButtonV2
                block
                wrapperStyle={{ width: "100%" }}
                style={values.expiration_date ? SELECTED_TOGGLE_STYLE : undefined}
                text={limitedButtonText}
                onClick={() => setDatePickerOpen(true)}
              />
            </Box>
          </HStack>
          <Datepicker
            controls={["calendar"]}
            anchor={datePickerAnchorRef.current}
            touchUi={isMobile}
            isOpen={datePickerOpen}
            showInput={false}
            onClose={() => setDatePickerOpen(false)}
            responsive={{medium: { display: "bottom" }, large: { display: "anchored" },}}
            buttons={[]}
            locale={locale[boxLocale]}
            value={values.expiration_date ? moment(values.expiration_date, "YYYY-MM-DD") : null}
            min={moment().add(1, "days")}
            returnFormat={"moment"}
            onChange={(e) => {
              setFieldValue("expiration_date", e.value ? moment(e.value).format("YYYY-MM-DD") : null);
              setDatePickerOpen(false);
            }}
          />
        </FormInput>

        <FormInput
          width={"100%"}
          label={t("discount")}
          numericOnly
          inputType={"float"}
          min={1}
          max={100}
          value={values.discount_amount}
          onChangeCallback={(v) => setFieldValue("discount_amount", v)}
          disabled={tieredActive}
          formatAddon={"%"}
          controls={true}
          className={"custom-input-number"}
          error={touched.discount_amount && errors.discount_amount ? errors.discount_amount : null}
        />

        <FormInput width={"100%"} label={t("limitPerUser")}>
          <AdvanceSelect
            options={limitPerUserOptions}
            initValue={values.limit_quantity === null ? PROMO_CODE_OPTION.UNLIMITED : values.limit_quantity}
            onChange={(val) => setFieldValue("limit_quantity", val?.value === PROMO_CODE_OPTION.UNLIMITED ? null : val?.value ?? null,)}
            hideCreateBtn
            dontSort
          />
        </FormInput>

        {showLocationField && (
          <FormInput width={"100%"} label={t("location")}>
            <AdvanceSelect
              options={locationOptions}
              initValue={values.location_id}
              onChange={(val) => setFieldValue("location_id", val?.value ?? null)}
              hideCreateBtn
              dontSort
            />
          </FormInput>
        )}
      </Box>

      <Divider style={{ margin: "1rem 0" }} />

      {allowRecurring && (
        <FormInput label={t("payment-option")} infoTooltip={t("paymentOptionTooltip")} width={isMobile ? '100%' : 'calc(50% - 0.7rem)'}>
          <HStack space={isMobile ? "1rem" : "0.5rem"}>
            <Box flex={1}>
              <CustomButtonV2
                block
                wrapperStyle={{ width: "100%" }}
                style={values.recurring_cycles === null ? SELECTED_TOGGLE_STYLE : undefined}
                additionalClassName={`payment-option-toggle ${values.recurring_cycles === null ? 'selected' : ''}`}
                text={t("regular")}
                disabled={isEditMode}
                onClick={() => selectRecurringOption(null)}
              />
            </Box>
            <Box flex={1}>
              <CustomButtonV2
                block
                wrapperStyle={{ width: "100%" }}
                style={values.recurring_cycles !== null ? SELECTED_TOGGLE_STYLE : undefined}
                additionalClassName={`payment-option-toggle ${values.recurring_cycles !== null ? 'selected' : ''}`}
                text={t("recurring")}
                disabled={isEditMode}
                onClick={() => selectRecurringOption(0)}
              />
            </Box>
          </HStack>
        </FormInput>
      )}
      {(!services || !membershipTypes) && <Spin size="small" />}
      <Box style={{display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", columnGap: "1rem", rowGap: "1rem",}}>
        {services && membershipTypes && Object.entries(visibleMembershipTypes).map(([type, memberships]) => (
          <FormInput key={type} width={"100%"} label={`${t(`promoCodeSelectLabel-${type}`)}${checkRequired("membership_types", validationObject,)}`}>
            <MultiSingleMenuWithSearch
              searchable
              allowClear
              labelProp={"name"}
              valueProp={"id"}
              options={memberships}
              value={selectedIdsByType[type] ?? []}
              onChange={(ids) => onMembershipSelectChange(type, ids)}
              disabled={memberships.length === 0}
              placeholder={t("search")}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {(selectedIdsByType[type]?.length ?? 0) !== memberships.length && (
                    <CustomButtonV2
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onMembershipSelectChange(type, memberships.map((m) => m.id),);
                      }}
                      text={t("selectAll")}
                      applyHoverOnText={true}
                      type={"link"}
                      iconProps={{iconColor: Colors.newDesign.mainBlue, iconHoverColor: Colors.newDesign.mainBlueHover,}}
                      wrapperStyle={{ marginTop: "8px" }}
                    />
                  )}
                </>
              )}
            />
          </FormInput>
        ))}
      </Box>

      {values.recurring_cycles !== null && (
        <FormInput width={"100%"} label={t("recurringRadioHeader")}>
          <Radio.Group value={tieredActive ? "tiered" : (values.recurring_cycles === 0 ? "all" : "first-n")} onChange={(e) => changeRecurringRadio(e.target.value)}>
            <VStack space={"0.5rem"}>
              <Radio value={"all"}>
                <HStack alignItems={"center"}>
                  <AppText>{t("allRecurring")}</AppText>
                  <InfoTooltip title={t("allRecurringTooltip")} />
                </HStack>
              </Radio>
              <Radio value={"first-n"}>
                <HStack space={"0.5rem"} alignItems={"center"}>
                  <FormInput numericOnly min={1} max={12} width={"70px"} value={cycles} onChangeCallback={onCyclesChange} containerProps={{ mb: 0 }} />
                  <HStack>
                    <AppText>{t("numFirstCycles")}</AppText>
                    <InfoTooltip title={t("numFirstCyclesTooltip")} />
                  </HStack>
                </HStack>
              </Radio>
              {allowTieredPromoCodes && (
                <Radio value={"tiered"}>
                  <HStack alignItems={"center"}>
                    <AppText>{t("tieredPromoCode")}</AppText>
                    <InfoTooltip title={t("tieredPromoCodesTooltip")} />
                  </HStack>
                </Radio>
              )}
              {tieredActive && (
                <VStack space={"1rem"}>
                  {values.tiered_promo_codes.map((tpc, i) => (
                    <HStack key={`tiered_promo_codes-${i}`} space={"0.5rem"} alignItems={'center'}>
                      <FormInput numericOnly
                                 min={1}
                                 max={100}
                                 width={"95px"}
                                 value={tpc.discount_amount}
                                 formatAddon={"%"}
                                 onChangeCallback={(v) => setTieredPromoCodeDiscount(i, v)}
                                 containerProps={{ mb: 0 }}
                                 error={touched.tiered_promo_codes?.[i]?.discount_amount && errors.tiered_promo_codes?.[i]?.discount_amount}
                      />
                      <AppText>{`${t("tieredPromoCodeCycle")} ${i + 1}`}</AppText>
                      {i > 0 && i === values.tiered_promo_codes.length - 1 && <CustomButtonV2 tooltipText={t("removeTieredCycle")} type={'link'} iconName={'fa-trash-can'} iconProps={{ iconSize: GlobalConsts.iconSize.XS, iconColor: Colors.error, iconHoverColor: Colors.error }} onClick={() => removeTieredPromoCode(i)}/>}
                    </HStack>
                  ))}
                  {values.tiered_promo_codes.length < TIERED_PROMO_CODE_MAX && <CustomButtonV2 onClick={addTieredPromoCode} text={`+ ${t("addNewTieredCycle")}`} applyHoverOnText={true} type={"link"} wrapperStyle={{width: 'fit-content'}} iconProps={{iconColor: Colors.newDesign.mainBlue, iconHoverColor: Colors.newDesign.mainBlueHover}}/>}
                </VStack>
              )}
            </VStack>
          </Radio.Group>
        </FormInput>
      )}
    </VStack>
  );
};

export default PromoCode;
